import { useState, useEffect, Fragment, useRef } from "react"
import { debounce } from "debounce"
import cc from "classcat"
import { NavLink, useLocation } from "react-router-dom"
import { Icon } from "~/Icon"
import { routes } from "../routes"

interface Props {
  className?: string
}

interface LocationState {
  title?: string
}

const isBelowNavThreshold = () => {
  const style = getComputedStyle(document.documentElement)
  const value = style.getPropertyValue("--navigation-visible")
  return Boolean(Number(value))
}

const navLinkClasses = {
  className:
    "menu__link menu__link--small hover:border-transparent hover:bg-white",
  activeClassName:
    "menu__link--active hover:border-transparent hover:bg-emphatic-700",
}

export const Navigation = ({ className }: Props) => {
  const location = useLocation<LocationState>()
  const mainNavLink = useRef<HTMLAnchorElement>(null)
  const navToggle = useRef<HTMLButtonElement>(null)

  const [navFocused, setNavFocused] = useState(false)
  const [navToggleable, setNavToggleable] = useState(isBelowNavThreshold())
  const [announcement, setAnnouncement] = useState("")

  const onScreenResize = debounce(
    () => {
      setNavToggleable(isBelowNavThreshold())
    },
    300,
    false
  )

  const unFocusNav = () => {
    if (navToggle && navToggle.current) {
      navToggle.current.focus()
      setNavFocused(false)
    }
  }

  const focusNav = () => {
    if (mainNavLink && mainNavLink.current) {
      mainNavLink.current.focus()
      setNavFocused(true)
    }
  }

  useEffect(() => {
    window.addEventListener("resize", onScreenResize)
    return () => {
      window.removeEventListener("resize", onScreenResize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const locationTitle =
      location.state && location.state.title ? location.state.title : "Home"
    unFocusNav()
    setAnnouncement(`You have navigated to the ${locationTitle} page`)
    document.title = `${locationTitle} - StackupUI`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  const handleClickMenuButton = () => {
    if (navFocused) {
      unFocusNav()
    } else {
      focusNav()
    }
  }

  const navIconPath = navFocused
    ? "M 7.21875 5.78125 L 5.78125 7.21875 L 14.5625 16 L 5.78125 24.78125 L 7.21875 26.21875 L 16 17.4375 L 24.78125 26.21875 L 26.21875 24.78125 L 17.4375 16 L 26.21875 7.21875 L 24.78125 5.78125 L 16 14.5625 Z "
    : "M 4 7 L 4 9 L 28 9 L 28 7 Z M 4 15 L 4 17 L 28 17 L 28 15 Z M 4 23 L 4 25 L 28 25 L 28 23 Z "

  const focusedClass = cc({ focused: navFocused })
  const isTabbable = !navToggleable || navFocused

  return (
    <Fragment>
      <div aria-live="assertive" aria-atomic="true" className="sr-only">
        {announcement}
      </div>

      {navToggleable && (
        <button
          ref={navToggle}
          onClick={handleClickMenuButton}
          title={navFocused ? "Hide navigation" : "Go to navigation"}
          className={`navigation-link ${focusedClass}`}
        >
          <svg
            role="presentation"
            className="navigation-link__icon"
            viewBox="0 0 32 32"
          >
            <path d={navIconPath} />
          </svg>
        </button>
      )}

      <div
        role="banner"
        className={`bg-neutral-100 overflow-y-auto ${className} ${focusedClass}`}
      >
        <h1 className="m-0 p-2 border-b border-neutral-200">
          <NavLink
            tabIndex={isTabbable ? 0 : -1}
            innerRef={mainNavLink}
            to="/"
            className="menu__title mb-0 hover:bg-white flex items-center justify-start"
          >
            <Icon name="stack" className="mr-2" />
            StackupUI
          </NavLink>
        </h1>

        <nav role="navigation" className="menu menu--vertical">
          {routes.map((section) => (
            <div
              className="p-2 border-b border-neutral-200"
              key={section.route}
            >
              <h2 className="menu__title menu__title--small">
                {section.title}
              </h2>
              <ul>
                {section.items.map((item) => (
                  <li className="menu__item" key={item.route}>
                    <NavLink
                      to={{
                        pathname: `/${section.route}/${item.route}`,
                        state: {
                          title: item.title,
                        },
                      }}
                      tabIndex={isTabbable ? 0 : -1}
                      {...navLinkClasses}
                    >
                      {item.title}
                      {item.beta && <span className="label ml-1">beta</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </div>
    </Fragment>
  )
}
